import { useEffect, useState } from 'react';
import { Box, Button, Container, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Info, Lock, RefreshCw, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';

import { isSignedIn, useAuth } from '@/features/scaffolding/components/AuthContext';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import AppPage from '@/shared/components/PageComponents/AppPage';
import { Loading } from '@/shared/components';
import PageHeader from '@/shared/components/PageHeader';
import { cx } from '@/styles/muiTheme';

import useGetClipExportQueueStatusQuery from '../hooks/useGetClipExportQueueStatusQuery';
import useClearJobsMutation from '../hooks/useClearJobsMutation';

type Status = 'waiting' | 'active' | 'delayed' | 'failed';

/**
 * Display order is the job lifecycle. Active is not clearable: the DELETE
 * endpoint never removes a job a worker is holding, so offering it would only
 * ever report 0 removed.
 */
const STATUSES: {
  key: Status;
  label: string;
  color: string;
  clearable: boolean;
  hint: string;
}[] = [
  { key: 'waiting', label: 'Waiting', color: cx.text.secondary, clearable: true, hint: 'Queued, not yet started' },
  { key: 'active', label: 'Active', color: cx.blue.hover, clearable: false, hint: 'Rendering now' },
  // Not a cx token — the design's one orange, used only here.
  { key: 'delayed', label: 'Delayed', color: '#F79609', clearable: true, hint: 'Scheduled to retry' },
  { key: 'failed', label: 'Failed', color: cx.red.main, clearable: true, hint: 'Exceeded retry limit' },
];

type Confirm = { statuses: Status[]; title: string; body: string; cta: string };

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const ManageQueuePage = () => {
  const title = 'Clip Export Queue';

  useDocumentTitle(title);

  const { sessionState } = useAuth();
  const isAuthReady = isSignedIn(sessionState);

  const clipExportQuery = useGetClipExportQueueStatusQuery();
  const clearJobsMutation = useClearJobsMutation();
  const { enqueueSnackbar } = useSnackbar();

  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [clearing, setClearing] = useState(false);

  // Ticks the "updated Xs ago" label between polls.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!isAuthReady || clipExportQuery.isLoading) {
    return (
      <AppPage title={title} requiredPermissions={['menu:admin:queues']}>
        <Loading />
      </AppPage>
    );
  }

  const counts = clipExportQuery.data?.overallCounts;
  const count = (s: Status) => counts?.[s] ?? 0;
  // The API's own `total` includes completed jobs; this page is about what is
  // still in flight, so it sums the four tiles instead.
  const total = STATUSES.reduce((sum, s) => sum + count(s.key), 0);
  const clearable = STATUSES.filter((s) => s.clearable && count(s.key) > 0).map((s) => s.key);
  const clearableTotal = clearable.reduce((sum, s) => sum + count(s), 0);

  const secs = Math.max(0, Math.round((now - clipExportQuery.dataUpdatedAt) / 1000));
  const updatedLabel = secs < 3 ? 'just now' : `${secs}s ago`;

  const askOne = (s: (typeof STATUSES)[number]) => {
    const n = count(s.key);
    const l = s.label.toLowerCase();
    setConfirm({
      statuses: [s.key],
      title: `Clear ${plural(n, `${l} job`)}?`,
      body: `All ${l} jobs will be removed from the export queue.`,
      cta: `Clear ${plural(n, 'job')}`,
    });
  };

  const askAll = () => {
    const active = count('active');
    setConfirm({
      statuses: clearable,
      title: `Clear ${plural(clearableTotal, 'job')}?`,
      body: `Waiting, delayed and failed jobs will be removed from the export queue. ${plural(active, 'active job')} will keep processing.`,
      cta: `Clear ${plural(clearableTotal, 'job')}`,
    });
  };

  // The endpoint clears one status per call, so "Clear all" is a sequence.
  const doClear = async () => {
    if (!confirm) return;
    setClearing(true);
    try {
      let removed = 0;
      for (const status of confirm.statuses) {
        const { removedCount } = await clearJobsMutation.mutateAsync({ status });
        removed += removedCount;
      }
      enqueueSnackbar(`${plural(removed, 'job')} cleared`, { variant: 'success', autoHideDuration: 2600 });
    } catch {
      enqueueSnackbar('Failed to clear jobs', { variant: 'error', autoHideDuration: 2600 });
    } finally {
      setClearing(false);
      setConfirm(null);
    }
  };

  const bigNumber = { fontFamily: cx.fontMono, fontSize: 36, lineHeight: '40px', fontWeight: 500 };

  return (
    <AppPage title={title} requiredPermissions={['menu:admin:queues']}>
      <Container maxWidth="lg" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader
          title={title}
          subtitle={
            <Stack direction="row" component="span" sx={{ alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: clipExportQuery.isError ? cx.red.main : cx.green }}
              />
              {clipExportQuery.isError ? "Couldn't reach the queue" : `Live · updated ${updatedLabel}`}
            </Stack>
          }
          action={
            <Stack direction="row" sx={{ gap: 1 }}>
              <Button variant="outlined" startIcon={<RefreshCw />} onClick={() => clipExportQuery.refetch()}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Trash2 />}
                onClick={askAll}
                disabled={clearableTotal === 0}
              >
                Clear all
              </Button>
            </Stack>
          }
        />

        <Stack sx={{ gap: 3 }}>
          <Stack sx={{ bgcolor: cx.bg.surface, borderRadius: '4px', p: 2.5, gap: 2 }}>
            <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
              <Typography sx={bigNumber}>{total}</Typography>
              <Typography sx={{ fontSize: 14, color: cx.text.tertiary }}>jobs in queue</Typography>
            </Stack>
            <Box sx={{ display: 'flex', height: 8, borderRadius: 100, overflow: 'hidden', bgcolor: cx.bg.surface3, gap: '2px' }}>
              {STATUSES.filter((s) => count(s.key) > 0).map((s) => (
                <Box key={s.key} sx={{ flex: count(s.key), bgcolor: s.color, transition: 'flex 300ms ease' }} />
              ))}
            </Box>
            {total === 0 && (
              <Typography variant="caption" sx={{ color: cx.text.muted }}>
                Queue is empty. New export jobs will appear here.
              </Typography>
            )}
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
            {STATUSES.map((s) => {
              const n = count(s.key);
              return (
                <Stack
                  key={s.key}
                  sx={{ bgcolor: cx.bg.surface2, border: `1px solid ${cx.border.subtle}`, borderRadius: '4px', p: 2, gap: 2 }}
                >
                  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', minHeight: 28 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: s.color }} />
                      <Typography variant="caption" sx={{ color: cx.text.secondary }}>
                        {s.label}
                      </Typography>
                    </Stack>
                    {s.clearable ? (
                      <IconButton
                        size="small"
                        title={`Clear ${s.label} jobs`}
                        onClick={() => askOne(s)}
                        disabled={n === 0}
                        sx={{
                          width: 28,
                          height: 28,
                          color: cx.text.tertiary,
                          '&.Mui-disabled': { color: cx.bg.elevated2 },
                          '&:hover': { bgcolor: cx.bg.elevated, color: cx.red.main },
                        }}
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </IconButton>
                    ) : (
                      <Stack
                        direction="row"
                        title="Jobs being processed can't be cleared"
                        sx={{ alignItems: 'center', gap: 0.5, fontSize: 10, lineHeight: '14px', color: cx.text.muted }}
                      >
                        <Lock size={12} strokeWidth={1.5} />
                        Processing
                      </Stack>
                    )}
                  </Stack>
                  <Typography sx={{ ...bigNumber, color: n === 0 ? cx.text.muted : cx.text.primary }}>{n}</Typography>
                  <Typography sx={{ fontSize: 11, lineHeight: '14px', color: cx.text.muted }}>{s.hint}</Typography>
                </Stack>
              );
            })}
          </Box>

          <Stack direction="row" sx={{ gap: 1, alignItems: 'flex-start', maxWidth: 720, color: cx.text.tertiary }}>
            <Info size={16} strokeWidth={1.5} style={{ flex: 'none' }} />
            <Typography variant="caption" sx={{ color: 'inherit', textWrap: 'pretty' }}>
              Clearing removes jobs from the queue only. Clip statuses in the database are not changed, so
              requeue affected clips afterward to keep them in sync.
            </Typography>
          </Stack>
        </Stack>
      </Container>

      <Dialog
        open={!!confirm}
        onClose={() => !clearing && setConfirm(null)}
        slotProps={{ paper: { sx: { width: '100%', maxWidth: 440, borderRadius: '6px', p: 3, gap: 2 } } }}
      >
        {confirm && (
          <>
            <Typography sx={{ fontSize: 18, lineHeight: '26px', fontWeight: 500 }}>{confirm.title}</Typography>
            <Typography variant="body2" sx={{ color: cx.text.secondary, textWrap: 'pretty' }}>
              {confirm.body}
            </Typography>
            <Stack sx={{ gap: 1, bgcolor: cx.bg.surface3, borderRadius: '4px', p: 1.5, color: cx.text.tertiary }}>
              <Typography variant="caption" sx={{ color: 'inherit', display: 'flex', gap: 1 }}>
                <Box component="span" sx={{ color: cx.gold }}>•</Box>
                Clip statuses in the database won't change. Requeue the affected clips to update them.
              </Typography>
              <Typography variant="caption" sx={{ color: 'inherit', display: 'flex', gap: 1 }}>
                <Box component="span" sx={{ color: cx.red.main }}>•</Box>
                This action cannot be undone.
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button variant="outlined" onClick={() => setConfirm(null)} disabled={clearing}>
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={doClear} disabled={clearing}>
                {confirm.cta}
              </Button>
            </Stack>
          </>
        )}
      </Dialog>
    </AppPage>
  );
};

export default ManageQueuePage;
